import { entityKind, type SQL as DrizzleSql } from "drizzle-orm";
import {
  type PgDatabase,
  type PgQueryResultHKT,
  type PgTransaction,
} from "drizzle-orm/pg-core";
import { type ExtractTablesWithRelations } from "drizzle-orm/relations";

import { ConfigurationError } from "../../../errors";
import { isSqlFragment } from "../../../query/sql-fragment";
import { shouldForceCustomPlan } from "../../../query/sql-intent";
import { getOrCreateLru } from "./lru";
import {
  type CompiledSqlQuery,
  compileQueryWithDialect,
  type ExecutableSql,
  type PreparedSqlStatement,
  type SqlExecutionAdapter,
  toDrizzleSql,
} from "./types";

/**
 * Edge-safe: this module is loaded on Cloudflare Workers / Vercel Edge
 * via `@neondatabase/serverless`, so it must not statically import any
 * `node:*` module. Use only platform-neutral primitives.
 */

type PgQueryResult = Readonly<{
  rows: readonly unknown[];
}>;

/**
 * The wire-protocol-shaped client the fast path calls. Both the wrapped
 * node-postgres and postgres-js clients normalize to this signature; what
 * differs is whether the underlying driver gets a server-side prepared
 * statement (node-postgres path) or relies on its own internal preparation
 * (postgres-js).
 */
type PgQueryClient = Readonly<{
  query: (
    sqlText: string,
    params: readonly unknown[],
  ) => Promise<PgQueryResult>;
  /**
   * Executes WITHOUT a named server-side prepared statement, so the
   * server plans against the actual parameter values on every call.
   * Used for statements marked `markForceCustomPlan` (see sql-intent).
   */
  queryUnnamed: (
    sqlText: string,
    params: readonly unknown[],
  ) => Promise<PgQueryResult>;
}>;

/**
 * Minimal shape of the node-postgres / @neondatabase/serverless client
 * we rely on. Both accept either positional `query(text, values)`
 * (unnamed) or the configuration-object form `query({name, text, values})`
 * which uses a server-side prepared statement keyed by `name`.
 */
type NodePgQueryConfig = Readonly<{
  name: string;
  text: string;
  values: readonly unknown[];
}>;

type NodePgClient = Readonly<{
  query: ((
    sqlText: string,
    params?: readonly unknown[],
  ) => Promise<PgQueryResult>) &
    ((config: NodePgQueryConfig) => Promise<PgQueryResult>);
}>;

/**
 * Minimal shape of the postgres-js tagged-template `Sql` client we rely on
 * for the raw fast path. `unsafe(sqlText, params)` is postgres-js's
 * parameterized-raw-SQL entry point and resolves to a row array.
 */
type PostgresJsClient = ((...args: readonly unknown[]) => unknown) &
  Readonly<{
    unsafe: (
      sqlText: string,
      params?: readonly unknown[],
      queryOptions?: Readonly<{ prepare?: boolean }>,
    ) => PromiseLike<readonly unknown[]>;
  }>;

type NeonHttpBatchClient = Readonly<{
  query: (
    sqlText: string,
    params?: readonly unknown[],
  ) => PromiseLike<readonly unknown[]>;
  transaction: (
    queries: readonly PromiseLike<readonly unknown[]>[],
  ) => Promise<readonly unknown[]>;
}>;

type PgSessionCarrier = Readonly<{
  client?: unknown;
  constructor?: Readonly<{ name?: string }>;
}>;

type PgClientCarrier = Readonly<{
  $client?: unknown;
  /** Drizzle's transaction handle keeps the pinned driver here. */
  session?: PgSessionCarrier;
  /** Defensive fallback for Drizzle builds exposing only the internal bag. */
  _?: Readonly<{
    session?: PgSessionCarrier;
  }>;
}>;

const INTERACTIVE_POSTGRES_DATABASE_KINDS = new Set([
  "NeonServerlessDatabase",
  "NodePgDatabase",
  "PgliteDatabase",
  "PostgresJsDatabase",
]);

function isRecognizedInteractivePostgresDatabase(db: AnyPgDatabase): boolean {
  const constructor = (
    db as unknown as Readonly<{
      constructor?: Readonly<Record<PropertyKey, unknown>>;
    }>
  ).constructor;
  return INTERACTIVE_POSTGRES_DATABASE_KINDS.has(
    String(constructor?.[entityKind]),
  );
}

export type AnyPgDatabase = PgDatabase<
  PgQueryResultHKT,
  Record<string, unknown>
>;

/**
 * A driver-agnostic Drizzle PostgreSQL transaction handle.
 *
 * Unlike {@link AnyPgDatabase}, this type cannot be satisfied by a root
 * database. It is the native handle exposed to adapter-aware stores and
 * accepted when adopting a caller-owned PostgreSQL transaction.
 */
export type AnyPgTransaction = PgTransaction<
  PgQueryResultHKT,
  Record<string, unknown>,
  ExtractTablesWithRelations<Record<string, unknown>>
>;

export type PostgresExecutionAdapter = SqlExecutionAdapter;

/**
 * Duck-typing primitive for the client-shape predicates: `value` carries a
 * callable `property`. Shared with the serialized-resource detection in
 * `../postgres.ts` so both spell "has this method" the same way.
 */
export function hasFunctionProperty<K extends string>(
  value: unknown,
  property: K,
): value is Readonly<Record<K, (...args: readonly unknown[]) => unknown>> {
  if (value === undefined || value === null) {
    return false;
  }
  const candidate = value as Readonly<Record<K, unknown>>;
  return typeof candidate[property] === "function";
}

function isPgNativeClient(candidate: unknown): candidate is NodePgClient {
  // pg `Pool` / `Client` and @neondatabase/serverless `Pool` / `Client`
  // are object instances with a `.query` method. We require non-callable
  // here so we don't accidentally swallow the postgres-js or neon-http
  // tagged-template clients (both callable, both also expose `.query`).
  return (
    typeof candidate === "object" && hasFunctionProperty(candidate, "query")
  );
}

function isPgliteClient(candidate: unknown): candidate is NodePgClient {
  // PGlite (Postgres-in-WASM) exposes `.query(sql, params, options)` like
  // node-postgres and so also satisfies `isPgNativeClient` — but it does NOT
  // accept the `{name, text, values}` named-statement config form. Passing
  // that object doesn't merely fail the call: it desyncs PGlite's single
  // wire connection, so every subsequent query errors with `08P01`. Detect
  // PGlite structurally via `dumpDataDir` (a PGlite-specific method absent on
  // pg `Pool`/`Client`) so `resolvePgClient` can route it to the unnamed
  // positional wrapper regardless of `prepareStatements`. Duck-typed with no
  // static import, keeping this module dependency-free and edge-safe.
  return (
    typeof candidate === "object" &&
    hasFunctionProperty(candidate, "query") &&
    hasFunctionProperty(candidate, "dumpDataDir")
  );
}

/**
 * PGlite 0.5.x serializes the wire-protocol parameter count as a signed
 * 16-bit integer. At 32,768 binds it silently returns no rows and leaves the
 * client returning false-empty results, so the advertised budget must stay at
 * the largest signed 16-bit value.
 */
export const PGLITE_MAX_BIND_PARAMETERS = 32_767;

/** Returns the PGlite client that owns a root Drizzle database, if present. */
export function getPgliteClient(db: AnyPgDatabase): object | undefined {
  const client = (db as PgClientCarrier).$client;
  return isPgliteClient(client) ? client : undefined;
}

/** Detects a root Drizzle database backed by PGlite without importing it. */
export function isPgliteDatabase(db: AnyPgDatabase): boolean {
  return getPgliteClient(db) !== undefined;
}

function transactionSession(
  carrier: PgClientCarrier,
): PgSessionCarrier | undefined {
  return carrier.session ?? carrier._?.session;
}

function hasPgliteSession(carrier: PgClientCarrier): boolean {
  return transactionSession(carrier)?.constructor?.name === "PgliteSession";
}

/**
 * Single owner of "this callable client is postgres-js". Shared with the
 * serialized-resource detection in `../postgres.ts`, which reads `options.max`
 * off the same client shape, so the fast path and the marking predicate cannot
 * drift about which callables are postgres-js.
 */
export function isPostgresJsClient(
  candidate: unknown,
): candidate is PostgresJsClient {
  // postgres-js's tagged-template Sql is callable, has `.unsafe` (raw
  // parameterized executor), and has `.begin` (transaction starter). The
  // transaction-scoped `TransactionSql` replaces `.begin` with `.savepoint`;
  // accept either so a Drizzle transaction keeps the fast path pinned.
  // Neon HTTP is also callable + has `.unsafe`, but that `.unsafe` is a
  // fragment builder rather than a query executor, so we discriminate
  // on `.begin` / `.savepoint` (postgres-js only).
  return (
    typeof candidate === "function" &&
    hasFunctionProperty(candidate, "unsafe") &&
    (hasFunctionProperty(candidate, "begin") ||
      hasFunctionProperty(candidate, "savepoint"))
  );
}

/**
 * @neondatabase/serverless's `neon(url)` HTTP-only tagged-template
 * function. Distinguishing markers vs postgres-js: `.transaction`
 * (an HTTP-batch transaction submitter) instead of `.begin`. Its
 * `.query`/`.unsafe` methods have different signatures from pg's, so
 * the fast path can't drive them safely — Drizzle's neon-http session
 * handles them correctly via the `db.execute` slow path.
 *
 * Exported so the backend factory can refuse interactive transactions when
 * this driver is detected (HTTP cannot hold a multi-statement session).
 */
export function isNeonHttpClient(db: AnyPgDatabase): boolean {
  const candidate = (db as PgClientCarrier).$client;
  return (
    typeof candidate === "function" &&
    hasFunctionProperty(candidate, "transaction") &&
    !hasFunctionProperty(candidate, "begin")
  );
}

/**
 * Returns the Neon HTTP client only when its current query method is present.
 * Older Neon HTTP releases expose `transaction()` but not `query()`; those
 * clients remain correctly classified as Neon HTTP (and therefore avoid the
 * node-postgres fast path), but cannot safely provide this batch surface.
 */
function resolveNeonHttpBatchClient(
  db: AnyPgDatabase,
): NeonHttpBatchClient | undefined {
  if (!isNeonHttpClient(db)) return undefined;
  const candidate = (db as PgClientCarrier).$client;
  if (
    typeof candidate !== "function" ||
    !hasFunctionProperty(candidate, "query") ||
    !hasFunctionProperty(candidate, "transaction")
  ) {
    return undefined;
  }
  return candidate as unknown as NeonHttpBatchClient;
}

/**
 * Default cap on the number of distinct SQL strings tracked for
 * server-side prepared statements. Mirrors the SQLite adapter's
 * `DEFAULT_PREPARED_STATEMENT_CACHE_MAX`. High-cardinality SQL text —
 * variable-length IN-list expansions, generated aliases, custom
 * `backend.execute()` calls — would otherwise grow this map (and the
 * matching per-session prepared-statement memory inside PostgreSQL)
 * without bound. The LRU bounds only TypeGraph's in-process SQL-to-name
 * lookup; safe names are never recycled, so it cannot bound prepared
 * statements retained by a live PostgreSQL connection.
 */
const DEFAULT_POSTGRES_STATEMENT_CACHE_MAX = 256;

/**
 * Module-scope cache mapping each unique SQL text to a stable
 * prepared-statement name. PostgreSQL only requires statement names to
 * be unique per session, so a monotonic counter is sufficient — and
 * avoids depending on a hash primitive (`node:crypto` doesn't exist on
 * Cloudflare Workers, Web Crypto's digest is async).
 *
 * The cache is module-scope on purpose: pg `Pool` reuses connections,
 * and the same SQL text needs the same statement name across every
 * adapter that might end up driving the same connection. Without
 * shared naming, two adapters could try to register two different
 * statements under the same name on a recycled connection.
 *
 * Bounded by `cacheMax` per call (LRU eviction). The counter is
 * deliberately independent of the cache size so eviction never
 * recycles a name — a recycled name could collide with a still-cached
 * statement on a long-lived pg connection ("prepared statement
 * already exists" error with different text).
 */
const STATEMENT_NAME_CACHE = new Map<string, string>();
let nextStatementId = 0;

function statementNameFor(sqlText: string, cacheMax: number): string {
  return getOrCreateLru(STATEMENT_NAME_CACHE, sqlText, cacheMax, () => {
    const name = `tg_${nextStatementId}`;
    nextStatementId += 1;
    return name;
  });
}

/**
 * Test-only: reset the module-scope statement-name state. Used to
 * isolate tests that assert on counter values or eviction behavior.
 */
export function resetStatementNameCacheForTests(): void {
  STATEMENT_NAME_CACHE.clear();
  nextStatementId = 0;
}

/**
 * Walks a row and replaces any `Date` instance with its ISO-string
 * equivalent. Used to normalize node-postgres / neon-serverless output
 * when we bypass Drizzle's session: pg's default type parsers return
 * `Date` objects for `timestamptz` / `timestamp` / `date` columns, but
 * TypeGraph's row contract everywhere downstream is "timestamps come
 * back as ISO strings." Drizzle's session installs a per-query type
 * override that does the same thing; this is the dependency-free,
 * edge-safe equivalent.
 *
 * O(columns × rows) — negligible for the row counts TypeGraph queries
 * return (typical: 1–100 rows × 5–30 columns).
 */
function normalizeRow(row: unknown): unknown {
  if (row === null || typeof row !== "object") return row;
  let mutated: Record<string, unknown> | undefined;
  for (const key of Object.keys(row)) {
    const value = (row as Record<string, unknown>)[key];
    if (value instanceof Date) {
      mutated ??= { ...(row as Record<string, unknown>) };
      mutated[key] = value.toISOString();
    }
  }
  return mutated ?? row;
}

function normalizeRows(rows: readonly unknown[]): readonly unknown[] {
  // Single-pass scan; only allocate a new array if any row contained a
  // Date that needed normalizing. Most rows on most queries don't.
  let mutated: unknown[] | undefined;
  for (let index = 0; index < rows.length; index += 1) {
    const original = rows[index];
    const normalized = normalizeRow(original);
    if (normalized !== original) {
      mutated ??= [...rows] as unknown[];
      mutated[index] = normalized;
    }
  }
  return mutated ?? rows;
}

/**
 * Wraps a node-postgres / neon-serverless client so every call goes
 * through a server-side prepared statement keyed by a stable name. The
 * first call on each connection parses + plans + executes; subsequent
 * calls with the same statement name reuse the cached plan and skip
 * both parse and plan phases. Measurable on multi-CTE TypeGraph
 * queries: 3-hop drops from ~7-12ms (parse + plan + execute every call)
 * to ~0.8ms median (execute only).
 *
 * Returned rows are normalized so that `Date` objects from default pg
 * type parsers become ISO strings, matching the row shape Drizzle's
 * session would produce. Without this, `ctx.<alias>.meta.createdAt`
 * style accessors in the SELECT-result path return Date instances,
 * breaking user code that expects strings.
 */
function wrapNodePgClient(
  client: NodePgClient,
  cacheMax: number,
): PgQueryClient {
  return {
    async query(
      sqlText: string,
      params: readonly unknown[],
    ): Promise<PgQueryResult> {
      const result = await client.query({
        name: statementNameFor(sqlText, cacheMax),
        text: sqlText,
        values: params,
      });
      return { rows: normalizeRows(result.rows) };
    },
    async queryUnnamed(
      sqlText: string,
      params: readonly unknown[],
    ): Promise<PgQueryResult> {
      const result = await client.query(sqlText, params);
      return { rows: normalizeRows(result.rows) };
    },
  };
}

/**
 * Variant that bypasses server-side prepared statements entirely.
 * Required for pgbouncer in transaction-pool mode (named statements
 * registered on one backend connection aren't visible on the next),
 * and useful when memory-sensitive workloads can't afford to keep
 * prepared statements warm across the pool.
 *
 * Same Date→ISO normalization as `wrapNodePgClient` so the row contract
 * downstream stays identical.
 */
function wrapNodePgClientUnnamed(client: NodePgClient): PgQueryClient {
  async function queryUnnamed(
    sqlText: string,
    params: readonly unknown[],
  ): Promise<PgQueryResult> {
    const result = await client.query(sqlText, params);
    return { rows: normalizeRows(result.rows) };
  }
  return {
    query: queryUnnamed,
    queryUnnamed,
  };
}

function adaptPostgresJsClient(sql: PostgresJsClient): PgQueryClient {
  async function query(
    sqlText: string,
    params: readonly unknown[],
  ): Promise<PgQueryResult> {
    // postgres-js handles its own statement preparation internally
    // (controlled by the `prepare` connection option, default true),
    // so we don't need to name statements here ourselves.
    const rows = await sql.unsafe(sqlText, params);
    return { rows };
  }
  return {
    query,
    async queryUnnamed(
      sqlText: string,
      params: readonly unknown[],
    ): Promise<PgQueryResult> {
      // Per-call opt-out of postgres-js's internal preparation
      // (verified: `unsafe(text, params, { prepare: false })` leaves
      // pg_prepared_statements empty), so custom-plan-preferring
      // statements are re-planned against their actual parameters on
      // every call — same contract as the node-postgres unnamed path.
      const rows = await sql.unsafe(sqlText, params, { prepare: false });
      return { rows };
    },
  };
}

/**
 * Resolves a Drizzle-wrapped PostgreSQL client to a uniform `{query}` shape
 * that the fast path can call. Supports:
 *
 * - `drizzle-orm/node-postgres` (pg Pool / Client) — wrapped to use
 *   server-side prepared statements with stable counter-derived names
 *   keyed by the SQL text (see `statementNameFor`). When
 *   `prepareStatements` is `false`, falls back to unnamed
 *   `client.query(text, values)` for pgbouncer transaction-pool
 *   compatibility.
 * - `drizzle-orm/neon-serverless` (@neondatabase/serverless Pool) —
 *   pg-Pool-compatible, takes the same wrapper as node-postgres
 * - `drizzle-orm/postgres-js` (postgres-js tagged-template Sql) — adapted
 *   via `.unsafe(sql, params)`. Safe because Drizzle installs transparent
 *   parsers on the same client instance at `drizzle()` time, so direct
 *   calls and Drizzle-routed calls produce identical row shapes.
 * - `drizzle-orm/pglite` (PGlite, Postgres-in-WASM) — routed to the unnamed
 *   wrapper unconditionally. PGlite's `.query` has no named-statement config
 *   form (and server-side prepared statements buy nothing for an in-process
 *   engine), so `prepareStatements` is ignored for it.
 *
 * Returns `undefined` for `drizzle-orm/neon-http` and any other driver we
 * don't recognize; callers fall back to `db.execute(sql)`, which goes
 * through the Drizzle session and is always correct (just without the
 * server-side prepared-statement perf win).
 */
function resolvePgClient(
  db: AnyPgDatabase,
  prepareStatements: boolean,
  cacheMax: number,
  useTransactionClient: boolean,
): PgQueryClient | undefined {
  // Order matters: neon-http and postgres-js are both callable + have
  // `.unsafe`, but neon-http's `.unsafe` is a fragment builder (not a
  // query executor) and its `.query` doesn't accept the {name, text,
  // values} config form. Skip the fast path entirely for neon-http and
  // let Drizzle's session route the call.
  if (isNeonHttpClient(db)) {
    return undefined;
  }
  const carrier = db as PgClientCarrier;
  // Top-level Drizzle databases expose `$client`. Transaction objects do not;
  // they retain the exact pinned pool/client transaction on `session.client`.
  // Reading that structural internal is what keeps trusted import on the same
  // connection that will COMMIT/ROLLBACK, instead of accidentally using the
  // outer pool while a transaction is open.
  const client =
    carrier.$client ??
    (useTransactionClient ? transactionSession(carrier)?.client : undefined);
  // Must precede `isPgNativeClient`: PGlite matches it too (object with
  // `.query`), but only the unnamed wrapper is safe for it (see
  // `isPgliteClient`).
  if (
    isPgliteClient(client) ||
    (useTransactionClient && hasPgliteSession(carrier))
  ) {
    if (!isPgNativeClient(client)) return undefined;
    return wrapNodePgClientUnnamed(client);
  }
  if (isPgNativeClient(client)) {
    return prepareStatements ?
        wrapNodePgClient(client, cacheMax)
      : wrapNodePgClientUnnamed(client);
  }
  if (isPostgresJsClient(client)) {
    // postgres-js handles its own preparation internally (controlled
    // by the driver's own `prepare` connection option) — the
    // `prepareStatements` flag here only affects the node-postgres
    // path.
    return adaptPostgresJsClient(client);
  }
  return undefined;
}

/**
 * Normalizes the result shape of `db.execute(sql)` across drivers.
 *
 * - `drizzle-orm/node-postgres` / `drizzle-orm/neon-serverless` return a
 *   pg-style `{ rows, rowCount, ... }` object.
 * - `drizzle-orm/postgres-js` returns the raw postgres-js result, which is
 *   a plain array of rows (with extra non-enumerable properties like
 *   `count` / `command`).
 *
 * The backend contract is "a row array." Normalize here so every caller
 * downstream can assume the same shape.
 */
async function executeDrizzleQuery<TRow>(
  db: AnyPgDatabase,
  query: DrizzleSql,
): Promise<readonly TRow[]> {
  const result = await db.execute(query);
  if (Array.isArray(result)) {
    return result as readonly TRow[];
  }
  return (result as Readonly<{ rows: readonly TRow[] }>).rows;
}

function createPgPreparedStatement(
  pgClient: PgQueryClient,
  sqlText: string,
  maxBindParameters: number | undefined,
): PreparedSqlStatement {
  return {
    async execute<TRow>(params: readonly unknown[]): Promise<readonly TRow[]> {
      assertWithinBindParameterLimit(params.length, maxBindParameters);
      const result = await pgClient.query(sqlText, params);
      return result.rows as readonly TRow[];
    },
  };
}

/**
 * Tuning options for the PostgreSQL execution fast path.
 *
 * Defaults are correct for `drizzle-orm/node-postgres` against a
 * standalone PostgreSQL or session-pooled deployment. Override when:
 *
 * - **Pooling through pgbouncer in transaction-pool mode.** Set
 *   `prepareStatements: false`. Named statements registered on one
 *   pooled backend connection aren't visible to the next, so the
 *   server-side prepared-statement cache must be off.
 * - **High-cardinality SQL text.** If the application emits many distinct
 *   compiled SQL strings (variable-length IN-list expansions, custom
 *   `backend.execute()` calls, generated aliases), set
 *   `prepareStatements: false` to avoid retaining one prepared statement per
 *   distinct SQL text on every live connection.
 */
export type PostgresExecutionAdapterOptions = Readonly<{
  /**
   * When `true` (default), node-postgres / neon-serverless calls go
   * through `client.query({name, text, values})` so PostgreSQL caches
   * the parsed/planned statement per session. Set to `false` to issue
   * unnamed `client.query(text, values)` calls — required for
   * pgbouncer transaction-pool mode.
   */
  prepareStatements?: boolean;
  /**
   * Maximum number of distinct SQL strings retained in TypeGraph's in-process
   * SQL-to-name lookup. Defaults to `DEFAULT_POSTGRES_STATEMENT_CACHE_MAX`
   * (256). Eviction never reuses a name, so it does not deallocate or bound
   * prepared statements retained by live PostgreSQL connections. Ignored when
   * `prepareStatements` is `false`.
   */
  preparedStatementCacheMax?: number;
  /**
   * Maximum parameters that one statement may dispatch. Backend factories
   * pass their normalized capability here so every execution surface has a
   * final fail-loud guard after batch sizing.
   */
  maxBindParameters?: number;
  /**
   * @internal Affirmative factory authorization for a root interactive atomic
   * program. The adapter must not infer transaction isolation from methods.
   */
  interactiveAtomicBatch?: boolean;
  /**
   * @internal Resolve Drizzle's pinned transaction client. Trusted import uses
   * this on its private adapter; ordinary transaction backends deliberately do
   * not expose a new raw-execution surface that would change routing behavior.
   */
  useTransactionClient?: boolean;
}>;

export function createPostgresExecutionAdapter(
  db: AnyPgDatabase,
  options: PostgresExecutionAdapterOptions = {},
): PostgresExecutionAdapter {
  const prepareStatements = options.prepareStatements ?? true;
  const cacheMax =
    options.preparedStatementCacheMax ?? DEFAULT_POSTGRES_STATEMENT_CACHE_MAX;
  const maxBindParameters = options.maxBindParameters;
  const neonHttpClient = resolveNeonHttpBatchClient(db);
  const pgClient = resolvePgClient(
    db,
    prepareStatements,
    cacheMax,
    options.useTransactionClient ?? false,
  );
  const canOpenInteractiveAtomicBatch =
    options.interactiveAtomicBatch === true &&
    neonHttpClient === undefined &&
    pgClient !== undefined &&
    options.useTransactionClient !== true &&
    isRecognizedInteractivePostgresDatabase(db) &&
    hasFunctionProperty(db, "transaction");

  function compile(query: ExecutableSql): CompiledSqlQuery {
    return compileQueryWithDialect(db, query, "PostgreSQL");
  }

  function assertCompiledWithinBindParameterLimit(
    compiledQuery: CompiledSqlQuery,
  ): void {
    assertWithinBindParameterLimit(
      compiledQuery.params.length,
      maxBindParameters,
    );
  }

  const executeNeonAtomicBatch =
    neonHttpClient === undefined ? undefined : (
      async function executeNeonAtomicBatch<TRow>(
        statements: readonly CompiledSqlQuery[],
      ): Promise<readonly (readonly TRow[])[]> {
        for (const statement of statements) {
          assertCompiledWithinBindParameterLimit(statement);
        }
        if (statements.length === 0) return [];
        const queries = statements.map((statement) =>
          neonHttpClient.query(statement.sql, statement.params),
        );
        const results = await neonHttpClient.transaction(queries);
        return results.map((result) => {
          if (
            typeof result === "object" &&
            result !== null &&
            "rows" in result &&
            Array.isArray((result as { rows?: unknown }).rows)
          ) {
            return normalizeRows(
              (result as { rows: readonly unknown[] }).rows,
            ) as readonly TRow[];
          }
          return Array.isArray(result) ?
              (normalizeRows(result) as readonly TRow[])
            : [];
        });
      }
    );

  const executeInteractiveAtomicBatch =
    canOpenInteractiveAtomicBatch ?
      async function executeInteractiveAtomicBatch<TRow>(
        statements: readonly CompiledSqlQuery[],
      ): Promise<readonly (readonly TRow[])[]> {
        for (const statement of statements) {
          assertCompiledWithinBindParameterLimit(statement);
        }
        if (statements.length === 0) return [];
        return db.transaction(async (tx) => {
          const transactionAdapter = createPostgresExecutionAdapter(tx, {
            ...options,
            useTransactionClient: true,
          });
          const executeCompiled = transactionAdapter.executeCompiled;
          if (executeCompiled === undefined) {
            throw new ConfigurationError(
              "PostgreSQL atomic batch execution could not bind to its transaction client.",
              {
                capability: "execution.atomicBatch",
                driver: transactionSession(tx as PgClientCarrier)?.constructor
                  ?.name,
              },
            );
          }
          const results: (readonly TRow[])[] = [];
          for (const statement of statements) {
            results.push(await executeCompiled<TRow>(statement));
          }
          return results;
        });
      }
    : undefined;
  const executeAtomicBatch =
    executeNeonAtomicBatch ?? executeInteractiveAtomicBatch;

  if (pgClient === undefined) {
    return {
      compile,
      async execute<TRow>(query: ExecutableSql): Promise<readonly TRow[]> {
        assertCompiledWithinBindParameterLimit(compile(query));
        return executeDrizzleQuery<TRow>(
          db,
          isSqlFragment(query) ? toDrizzleSql(query, "postgres") : query,
        );
      },
      ...(executeAtomicBatch === undefined ? {} : { executeAtomicBatch }),
    };
  }

  const pgQueryClient = pgClient;

  async function executeCompiled<TRow>(
    compiledQuery: CompiledSqlQuery,
  ): Promise<readonly TRow[]> {
    assertCompiledWithinBindParameterLimit(compiledQuery);
    const result = await pgQueryClient.query(
      compiledQuery.sql,
      compiledQuery.params,
    );
    return result.rows as readonly TRow[];
  }

  return {
    compile,
    async execute<TRow>(query: ExecutableSql): Promise<readonly TRow[]> {
      // Fast path: compile via Drizzle's dialect, then execute through
      // the wrapped client directly. Bypasses Drizzle's session
      // overhead (logging spans, plan-cache bookkeeping for typed
      // queries we don't use), and on node-postgres this also enables
      // the server-side prepared-statement path because the wrapped
      // client assigns each unique SQL a stable statement name.
      const compiled = compile(query);
      assertCompiledWithinBindParameterLimit(compiled);
      if (isSqlFragment(query) && shouldForceCustomPlan(query)) {
        const result = await pgQueryClient.queryUnnamed(
          compiled.sql,
          compiled.params,
        );
        return result.rows as readonly TRow[];
      }
      return executeCompiled<TRow>(compiled);
    },
    executeCompiled,
    ...(executeAtomicBatch === undefined ? {} : { executeAtomicBatch }),
    prepare(sqlText: string): PreparedSqlStatement {
      return createPgPreparedStatement(
        pgQueryClient,
        sqlText,
        maxBindParameters,
      );
    },
  };
}

function assertWithinBindParameterLimit(
  parameterCount: number,
  maxBindParameters: number | undefined,
): void {
  if (maxBindParameters === undefined || parameterCount <= maxBindParameters) {
    return;
  }
  throw new ConfigurationError(
    `PostgreSQL statement uses ${parameterCount} bound parameters, exceeding ` +
      `this backend's limit of ${maxBindParameters}.`,
    {
      capability: "maxBindParameters",
      maxBindParameters,
      parameterCount,
    },
    {
      suggestion:
        "Split the operation into smaller batches or lower the batch size before retrying.",
    },
  );
}
